import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { PageHeader } from '@/components/PageHeader';
import { KeyRound, UserCheck, UserX, LogIn, RefreshCw, ShieldAlert, Mail, FileDown, BellRing, Copy } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATI = {
  ATTIVO: { label: 'Attivo', cls: 'bg-green-100 text-green-800' },
  DISATTIVATO: { label: 'Creato, non attivo', cls: 'bg-yellow-100 text-yellow-800' },
  DA_CREARE: { label: 'Da creare', cls: 'bg-blue-100 text-blue-800' },
  SENZA_CONTATTI: { label: 'Senza email/PEC', cls: 'bg-red-100 text-red-800' },
  CONTATTO_CONDIVISO: { label: 'Contatto condiviso', cls: 'bg-orange-100 text-orange-800' },
  SENZA_TITOLARITA_ATTIVA: { label: 'Senza titolarità attiva', cls: 'bg-gray-100 text-gray-700' },
};

// Gestione degli accessi dei condomini. Nessuna operazione di questa pagina invia email, PEC o notifiche da sola.
// UNICA eccezione: il pannello «Lettera d'invito», che spedisce solo quando l'amministratore preme «Invia lettera per email»
// e conferma due volte (finestra di conferma + casella «Confermo di voler inviare ora»). Mai in automatico.
const quandoIt = (iso) => (iso ? new Date(iso).toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' }) : null);

export const CondominiAccessi = () => {
  const { token, selectedCondominium, entraComeCondomino } = useAuth();
  const condoId = selectedCondominium?.condominium?.id;
  const base = `${API_URL}/api/condominiums/${condoId}/accessi`;
  const intest = useMemo(() => ({ Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }), [token]);

  const [dati, setDati] = useState(null);
  const [filtro, setFiltro] = useState('');
  const [cerca, setCerca] = useState('');
  const [anteprima, setAnteprima] = useState(null);
  const [conCondivisi, setConCondivisi] = useState(false);
  const [conEsistenti, setConEsistenti] = useState(false);
  const [lavoro, setLavoro] = useState(false);
  const [dlgAttiva, setDlgAttiva] = useState(false);
  const [pwAttiva, setPwAttiva] = useState('');
  const [cambioObbl, setCambioObbl] = useState(true);
  const [dlgPw, setDlgPw] = useState(null);       // riga per cui si imposta la password
  const [pwSingola, setPwSingola] = useState('');
  const [pwGenerata, setPwGenerata] = useState('');
  const [invConferma, setInvConferma] = useState(false);   // doppia conferma dell'invio email della nuova password
  const [invEsito, setInvEsito] = useState('');
  useEffect(() => { setInvConferma(false); setInvEsito(''); }, [pwGenerata, dlgPw]);

  const chiama = useCallback(async (url, corpo) => {
    const res = await fetch(url, { method: corpo === undefined ? 'GET' : 'POST', headers: intest, body: corpo === undefined ? undefined : JSON.stringify(corpo) });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(typeof json.detail === 'string' ? json.detail : 'Operazione non riuscita');
    return json;
  }, [intest]);

  const carica = useCallback(async () => {
    if (!condoId) return;
    try { setDati(await chiama(base)); } catch (e) { toast.error(e.message); }
  }, [base, chiama, condoId]);

  useEffect(() => { carica(); }, [carica]);

  const righe = useMemo(() => (dati?.righe || []).filter((r) =>
    (!filtro || r.stato === filtro) &&
    (!cerca || `${r.nome} ${r.email || ''} ${r.pec || ''} ${(r.unita || []).join(' ')}`.toLowerCase().includes(cerca.toLowerCase()))
  ), [dati, filtro, cerca]);

  const esegui = async (fn) => {
    setLavoro(true);
    try { await fn(); } catch (e) { toast.error(e.message); }
    setLavoro(false);
  };

  const faiAnteprima = () => esegui(async () => { setConCondivisi(false); setConEsistenti(false); setAnteprima(await chiama(`${base}/crea`, { dry_run: true })); });

  const creaUtenti = () => esegui(async () => {
    const r = await chiama(`${base}/crea`, { dry_run: false, includi_condivisi: conCondivisi, collega_esistenti: conEsistenti });
    const nCond = (r.gruppi_condivisi || []).filter((g) => g.user_id).length;
    toast.success(`${(r.creati || []).length} utenti creati${nCond ? ` e ${nCond} account condivisi` : ''}${(r.collegati || []).length ? ` e ${(r.collegati || []).length} account già registrati collegati` : ''}, tutti NON attivi: nessuno può ancora entrare. Nessuna email inviata.`);
    setAnteprima(null);
    await carica();
  });

  const attiva = () => esegui(async () => {
    const r = await chiama(`${base}/attiva`, { password: pwAttiva, cambio_obbligatorio: cambioObbl });
    toast.success(`${r.attivati} utenti attivati. Nessuna email inviata: comunica tu la password.`);
    setDlgAttiva(false); setPwAttiva('');
    await carica();
  });

  const disattivaTutti = () => esegui(async () => {
    if (!window.confirm('Disattivare l\'accesso a tutti i condomini creati? Potranno essere riattivati.')) return;
    const r = await chiama(`${base}/disattiva`, {});
    toast.success(`${r.disattivati} utenti disattivati`);
    await carica();
  });

  const imposta = (genera) => esegui(async () => {
    const r = await chiama(`${base}/${dlgPw.user_id}/imposta-password`, genera ? { genera: true } : { password: pwSingola });
    // la password (generata o scritta) resta in finestra: si puo' copiare il messaggio o inviarlo per email con conferma
    setPwGenerata(genera ? r.password_generata : pwSingola);
    if (!genera) { toast.success('Password impostata. Nessuna email inviata finche\' non premi «Invia per email».'); setPwSingola(''); }
    await carica();
  });

  // Allinea l'indirizzo di accesso a email/PEC attuali in anagrafica. Prima mostra cosa cambierebbe (nessuna scrittura), poi chiede conferma.
  const riallineaAccesso = (riga) => esegui(async () => {
    const ant = await chiama(`${base}/${riga.user_id}/riallinea-accesso`, { dry_run: true });
    if (ant.esito === 'GIA_ALLINEATO') { toast.success('Già allineato'); return; }
    if (ant.esito !== 'DA_RIALLINEARE') { toast.error(`${riga.nome}: ${ant.dettaglio || ant.esito}`); return; }
    const ok = window.confirm(`L'accesso di ${riga.nome} passa da\n${ant.prima.email}\na\n${ant.dopo.email}\n(indirizzi validi per entrare: ${ant.dopo.login_aliases.join(', ')}).\n\nGli indirizzi vecchi non entreranno più.${ant.password_annullata ? ' La password attuale viene ANNULLATA (non era di questa persona): nessuno entra finché non ne generi una nuova.' : ' La password non cambia.'} Poi genera la password e invia la lettera. Procedo?`);
    if (!ok) return;
    const r = await chiama(`${base}/${riga.user_id}/riallinea-accesso`, { dry_run: false });
    if (r.esito === 'RIALLINEATO') toast.success(`Accesso di ${riga.nome} allineato a ${r.dopo.email}. Ora genera la password e invia la lettera.`);
    else toast.error(r.dettaglio || r.esito);
    await carica();
  });

  // Invia per email la password appena impostata: stessa rotta e stessa email della lettera d'invito, UN solo destinatario, con conferma esplicita.
  const inviaMailPassword = () => esegui(async () => {
    if (!invConferma || !dlgPw?.user_id || !pwGenerata) return;
    const r = await chiama(`${base}/lettera/invia`, { user_ids: [dlgPw.user_id], password: pwGenerata, conferma: true, reinvio: true });
    const e = (r.esiti || [])[0] || {};
    if (e.esito === 'SENT') { setInvEsito(`Inviata a ${e.indirizzo}${e.profilo === 'PEC' ? ' (PEC)' : ''}.`); toast.success('Email inviata'); }
    else setInvEsito(`Non inviata: ${e.dettaglio || e.esito || 'esito sconosciuto'}`);
  });

  // Revoca / ripristino dell'accesso di UN condomino (vale subito; l'account resta e l'amministratore puo' sempre entrare come lui)
  const cambiaStato = (riga, attivo) => esegui(async () => {
    let motivo = null;
    if (!attivo) {
      motivo = window.prompt(`Disattivare l'accesso di ${riga.nome}? Non potrà più entrare finché non lo riattivi. Motivo (facoltativo):`, '');
      if (motivo === null) return;
    }
    await chiama(`${base}/${riga.user_id}/stato`, { attivo, motivo });
    toast.success(attivo ? `Accesso di ${riga.nome} riattivato` : `Accesso di ${riga.nome} disattivato`);
    await carica();
  });

  const accediCome = (riga) => esegui(async () => {
    const r = await chiama(`${base}/${riga.user_id}/accedi-come`, {});
    entraComeCondomino(r.access_token);
  });

  // ---- Lettera d'invito ----------------------------------------------------------------------------------------------
  const [lett, setLett] = useState(null);                 // {destinatari, max_per_invio, smtp, oggetto}
  const [lettPw, setLettPw] = useState('');               // mai precompilata, mai salvata, svuotata dopo l'invio
  const [lettSel, setLettSel] = useState([]);
  const [lettAnt, setLettAnt] = useState(null);
  const [dlgInvio, setDlgInvio] = useState(false);
  const [confermoInvio, setConfermoInvio] = useState(false);
  const [esitiInvio, setEsitiInvio] = useState(null);

  const caricaLettera = useCallback(async () => {
    if (!condoId) return;
    try { setLett(await chiama(`${base}/lettera/stato`)); } catch (e) { toast.error(e.message); }
  }, [base, chiama, condoId]);
  useEffect(() => { caricaLettera(); }, [caricaLettera]);

  const primoSel = lettSel[0] || '';
  useEffect(() => {
    if (!condoId) return undefined;
    let vivo = true;
    chiama(`${base}/lettera/anteprima${primoSel ? `?user_id=${encodeURIComponent(primoSel)}` : ''}`)
      .then((r) => { if (vivo) setLettAnt(r); }).catch(() => { if (vivo) setLettAnt(null); });
    return () => { vivo = false; };
  }, [base, chiama, condoId, primoSel]);

  const lettRighe = lett?.destinatari || [];
  const lettAttivi = lettRighe.filter((r) => r.attivo);
  const lettMax = lett?.max_per_invio || 80;
  const lettGia = lettRighe.filter((r) => lettSel.includes(r.user_id) && r.gia_inviata);
  const lettPwOk = lettPw.trim().length >= 8;
  const smtpOk = Boolean(lett?.smtp?.email);
  const alternaSel = (id) => setLettSel((v) => (v.includes(id) ? v.filter((x) => x !== id) : [...v, id]));

  const scaricaLettere = () => esegui(async () => {
    const res = await fetch(`${base}/lettera/pdf`, { method: 'POST', headers: intest, body: JSON.stringify({ user_ids: lettSel, password: lettPw.trim() }) });
    if (!res.ok) {
      const j = await res.json().catch(() => ({}));
      throw new Error(typeof j.detail === 'string' ? j.detail : 'Operazione non riuscita');
    }
    const url = URL.createObjectURL(await res.blob());
    const a = document.createElement('a');
    a.href = url; a.download = 'lettere-invito-condomini.pdf';
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
    toast.success(`PDF pronto: ${lettSel.length} lettere, una per pagina. Non è stato inviato nulla.`);
  });

  const inviaLettere = () => esegui(async () => {
    const r = await chiama(`${base}/lettera/invia`, { user_ids: lettSel, password: lettPw.trim(), conferma: true, reinvio: lettGia.length > 0 });
    setEsitiInvio(r);
    setDlgInvio(false); setConfermoInvio(false); setLettPw(''); setLettSel([]);
    const altri = r.richiesti - r.inviate;
    toast.success(`${r.inviate} lettere inviate${altri ? `, ${altri} non inviate (vedi l'elenco)` : ''}.`);
    await caricaLettera();
  });

  const c = dati?.conteggi || {};

  return (
    <div className="space-y-6">
      <PageHeader title="Accessi dei condomini" description="Crea, attiva e gestisci gli utenti dei condomini. Non parte mai nulla da solo: la lettera d'invito si invia solo con il tuo comando esplicito." />

      <div className="rounded-md border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900 flex gap-2">
        <ShieldAlert className="h-4 w-4 mt-0.5 shrink-0" />
        <div>
          I utenti vengono creati <strong>non attivi</strong>: nessuno può entrare finché non premi «Attiva». Quando attivi, scegli tu la password uguale per tutti
          (provvisoria: al primo accesso ciascun condomino ne sceglie una sua). Le credenziali le comunichi tu: con la lettera d'invito qui sotto, quando decidi.
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Situazione</CardTitle>
          <CardDescription>{dati ? `${dati.totale} persone in anagrafica` : 'Caricamento…'}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {(dati?.reset_richiesti || 0) > 0 && (
            <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900" data-testid="avviso-reset-password">
              <BellRing className="h-4 w-4 mt-0.5 shrink-0" />
              <div><strong>{dati.reset_richiesti} {dati.reset_richiesti === 1 ? 'condomino ha' : 'condomini hanno'} chiesto una nuova password.</strong>{' '}
                Cerca la riga con «ha chiesto una nuova password», premi la chiave, genera la password e inviagliela per email dalla stessa finestra (o comunicagliela tu).</div>
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant={filtro === '' ? 'default' : 'outline'} onClick={() => setFiltro('')}>Tutte</Button>
            {Object.entries(STATI).map(([k, v]) => (
              <Button key={k} size="sm" variant={filtro === k ? 'default' : 'outline'} onClick={() => setFiltro(k)}>
                {v.label} ({c[k] || 0})
              </Button>
            ))}
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={carica} disabled={lavoro}><RefreshCw className="h-4 w-4 mr-1" />Aggiorna</Button>
            <Button onClick={faiAnteprima} disabled={lavoro}>1. Anteprima creazione utenti</Button>
            <Button onClick={() => setDlgAttiva(true)} disabled={lavoro || !(c.DISATTIVATO > 0)} variant="secondary"><UserCheck className="h-4 w-4 mr-1" />2. Attiva gli accessi</Button>
            <Button onClick={disattivaTutti} disabled={lavoro || !(c.ATTIVO > 0)} variant="outline"><UserX className="h-4 w-4 mr-1" />Disattiva tutti</Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2"><Mail className="h-4 w-4" />Lettera d'invito ai condomini</CardTitle>
          <CardDescription>
            Spiega come entrare e cosa fare nell'app. Puoi scaricarla in PDF (da stampare o spedire a mano) oppure inviarla per email: l'invio parte solo quando premi tu il tasto e confermi.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4 text-sm">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label>Anteprima del testo{lettAnt ? ` (oggetto: ${lettAnt.oggetto})` : ''}</Label>
              <pre className="max-h-72 overflow-auto whitespace-pre-wrap rounded-md border bg-muted/40 p-3 text-xs font-sans">{lettAnt ? lettAnt.corpo : 'Anteprima non disponibile'}</pre>
              <p className="text-xs text-muted-foreground">Nell'anteprima la password è sempre nascosta (••••••). {primoSel ? 'Il testo mostra il primo destinatario scelto.' : 'Scegli un destinatario per vedere nome e utente.'}</p>
            </div>
            <div className="space-y-3">
              <div>
                <Label htmlFor="lett-pw">Password provvisoria da scrivere nella lettera (almeno 8 caratteri)</Label>
                <Input id="lett-pw" type="text" value={lettPw} onChange={(e) => setLettPw(e.target.value)} autoComplete="off" spellCheck={false} placeholder="la stessa che hai usato per attivare gli accessi" />
                <p className="text-xs text-muted-foreground mt-1">Non viene salvata. Per l'email deve coincidere con la password attuale del condomino, altrimenti quella lettera non parte.</p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="outline" onClick={() => setLettSel(lettAttivi.filter((r) => !r.gia_inviata).slice(0, lettMax).map((r) => r.user_id))}>
                  Attivi non ancora invitati ({lettAttivi.filter((r) => !r.gia_inviata).length})
                </Button>
                <Button size="sm" variant="outline" onClick={() => setLettSel(lettAttivi.slice(0, lettMax).map((r) => r.user_id))}>Tutti gli attivi</Button>
                <Button size="sm" variant="outline" onClick={() => setLettSel([])}>Nessuno</Button>
              </div>
              <div className="max-h-52 overflow-auto rounded-md border divide-y">
                {lettRighe.length === 0 && <div className="p-3 text-muted-foreground">Nessun condomino creato. Crea gli utenti e attivali, poi torna qui.</div>}
                {lettRighe.map((r) => (
                  <label key={r.user_id} className={`flex items-center gap-2 px-3 py-1.5 ${r.attivo ? '' : 'opacity-50'}`}>
                    <input type="checkbox" disabled={!r.attivo} checked={lettSel.includes(r.user_id)} onChange={() => alternaSel(r.user_id)} />
                    <span className="flex-1">{r.nome}<span className="text-xs text-muted-foreground"> · {r.email}</span></span>
                    <span className="text-xs text-muted-foreground">{r.canale === 'PEC' ? 'PEC' : 'posta'} · {r.ultimo_accesso ? `accesso ${quandoIt(r.ultimo_accesso)}` : 'mai entrato'}{r.ultima_attivita ? ` · attività ${quandoIt(r.ultima_attivita)}` : ''}{r.password_provvisoria ? ' · password provvisoria' : ''}</span>
                    {!r.attivo && <Badge className="bg-yellow-100 text-yellow-800">non attivo</Badge>}
                    {r.gia_inviata && <Badge className="bg-green-100 text-green-800">inviata il {(r.data_invio || '').slice(0, 10).split('-').reverse().join('/')}</Badge>}
                  </label>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">
                Scelti: {lettSel.length} (massimo {lettMax} per volta). Si può scegliere solo chi ha l'accesso attivo.
                {lettGia.length > 0 ? ` Attenzione: ${lettGia.length} l'hanno già ricevuta.` : ''}
              </p>
            </div>
          </div>
          {lett && !smtpOk && (
            <div className="rounded-md border border-red-300 bg-red-50 p-3 text-red-900">
              La posta in uscita non è configurata sul server: l'invio per email non è possibile. Puoi comunque scaricare le lettere in PDF.
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={scaricaLettere} disabled={lavoro || lettSel.length === 0 || !lettPwOk}><FileDown className="h-4 w-4 mr-1" />Scarica lettere PDF</Button>
            <Button onClick={() => { setConfermoInvio(false); setDlgInvio(true); }} disabled={lavoro || lettSel.length === 0 || lettSel.length > lettMax || !lettPwOk || !smtpOk}>
              <Mail className="h-4 w-4 mr-1" />Invia lettera per email
            </Button>
          </div>
          {esitiInvio && (
            <div className="rounded-md border p-3 space-y-1">
              <strong>Ultimo invio: {esitiInvio.inviate} inviate su {esitiInvio.richiesti} richieste</strong>
              <ul className="list-disc ml-5 text-xs">
                {esitiInvio.esiti.filter((x) => x.esito !== 'SENT').map((x) => <li key={x.user_id}>{x.indirizzo || x.user_id}: {x.esito}{x.dettaglio ? ` (${x.dettaglio})` : ''}</li>)}
                {esitiInvio.esiti.every((x) => x.esito === 'SENT') && <li>Tutte inviate.</li>}
              </ul>
              <Button size="sm" variant="outline" onClick={() => setEsitiInvio(null)}>Chiudi</Button>
            </div>
          )}
        </CardContent>
      </Card>

      {anteprima && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Anteprima: non è stato creato nulla</CardTitle>
            <CardDescription>
              {(anteprima.da_creare || []).length} utenti da creare · {(anteprima.conflitti || []).length} conflitti · {(anteprima.non_creabili || []).length} non creabili
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4 text-sm">
            {(anteprima.gruppi_condivisi || []).length > 0 && (
              <div className="rounded-md border border-orange-300 bg-orange-50 p-3 space-y-2">
                <strong>Contatti condivisi: {(anteprima.gruppi_condivisi || []).length} gruppi</strong>
                <p className="text-xs">Per ogni indirizzo si crea <b>un solo account</b>, collegato a tutte le persone elencate: chi entra vede la posizione di ciascuna di loro (quote, pagamenti, avvisi). Va bene per coniugi o comproprietari; non per persone senza rapporti fra loro.</p>
                <ul className="list-disc ml-5">
                  {anteprima.gruppi_condivisi.map((g) => (
                    <li key={g.login}>{g.login}: {g.persone.map((x) => `${x.nome}${x.unita?.length ? ` (${x.unita.join(', ')})` : ''}`).join(' + ')}</li>
                  ))}
                </ul>
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={conCondivisi} onChange={(e) => setConCondivisi(e.target.checked)} />
                  Crea anche questi account condivisi
                </label>
              </div>
            )}
            {(anteprima.da_collegare || []).length > 0 && (
              <div className="rounded-md border border-blue-300 bg-blue-50 p-3 space-y-2">
                <strong>Già registrati da soli: {(anteprima.da_collegare || []).length}</strong>
                <p className="text-xs">Si erano iscritti per conto loro, hanno una password propria e non sono ancora collegati a nessuna scheda. Collegandoli, l'account diventa il loro accesso al condominio ma resta <b>non attivo</b> finché non attivi gli accessi.</p>
                <ul className="list-disc ml-5">
                  {anteprima.da_collegare.map((x) => <li key={x.person_id}>{x.nome}{x.unita?.length ? ` (${x.unita.join(', ')})` : ''} · {x.login}{x.registrato_il ? ` · registrato il ${x.registrato_il.slice(0, 10).split('-').reverse().join('/')}` : ''}</li>)}
                </ul>
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={conEsistenti} onChange={(e) => setConEsistenti(e.target.checked)} />
                  Collega questi account già registrati
                </label>
              </div>
            )}
            {(anteprima.conflitti || []).length > 0 && (
              <div><strong>Conflitti</strong> (indirizzo già usato da un altro utente):
                <ul className="list-disc ml-5">{anteprima.conflitti.map((x) => <li key={x.person_id}>{x.nome} · {x.login}</li>)}</ul></div>
            )}
            {(anteprima.non_creabili || []).length > 0 && (
              <div><strong>Non creabili</strong> (senza email/PEC):
                <ul className="list-disc ml-5">{anteprima.non_creabili.map((x, i) => <li key={i}>{x.nome} · {STATI[x.stato]?.label || x.stato}</li>)}</ul></div>
            )}
            <div className="flex gap-2">
              <Button onClick={creaUtenti} disabled={lavoro || !((anteprima.da_creare || []).length || (conCondivisi && (anteprima.gruppi_condivisi || []).length) || (conEsistenti && (anteprima.da_collegare || []).length))}>
                Crea {(anteprima.da_creare || []).length}{conCondivisi ? ` + ${(anteprima.gruppi_condivisi || []).length} condivisi` : ''}{conEsistenti ? ` + ${(anteprima.da_collegare || []).length} già registrati` : ''} utenti (non attivi)
              </Button>
              <Button variant="outline" onClick={() => setAnteprima(null)}>Chiudi</Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="pt-6 space-y-3">
          <Input placeholder="Cerca per nome, email, PEC, unità" value={cerca} onChange={(e) => setCerca(e.target.value)} className="max-w-sm" />
          <Table>
            <TableHeader>
              <TableRow><TableHead>Nome</TableHead><TableHead>Unità</TableHead><TableHead>Email / PEC</TableHead><TableHead>Stato</TableHead><TableHead>Accesso e attività</TableHead><TableHead>Informativa privacy</TableHead><TableHead className="text-right">Azioni</TableHead></TableRow>
            </TableHeader>
            <TableBody>
              {righe.map((r) => (
                <TableRow key={r.person_id}>
                  <TableCell className="font-medium">{r.nome}</TableCell>
                  <TableCell>{(r.unita || []).join(', ') || '—'}</TableCell>
                  <TableCell className="text-xs">{r.email || '—'}{r.pec ? <><br />PEC: {r.pec}</> : null}</TableCell>
                  <TableCell>
                    <Badge className={STATI[r.stato]?.cls}>{STATI[r.stato]?.label || r.stato}</Badge>
                    {r.cambio_password_richiesto && r.user_id ? <div className="text-xs text-muted-foreground mt-1">password provvisoria</div> : null}
                    {r.accesso_disallineato ? <Badge className="mt-1 bg-red-100 text-red-900" title={`L'accesso è ${r.login}; in anagrafica risulta ${r.login_atteso}`}>accesso non allineato all'anagrafica</Badge> : null}
                    {r.accesso_problema ? <div className="text-xs text-red-800 mt-1">{r.accesso_problema}</div> : null}
                    {r.reset_richiesto_il ? <Badge className="mt-1 bg-amber-100 text-amber-900">ha chiesto una nuova password · {quandoIt(r.reset_richiesto_il)}</Badge> : null}
                  </TableCell>
                  <TableCell className="text-xs">{r.user_id ? (r.ultimo_accesso ? <>ultimo accesso {quandoIt(r.ultimo_accesso)} ({r.n_accessi}){r.ultima_attivita ? <><br />ultima attività {quandoIt(r.ultima_attivita)}</> : null}</> : 'mai entrato') : '—'}</TableCell>
                  <TableCell className="text-xs">{r.user_id ? (r.informativa_accettata_il ? `accettata il ${r.informativa_accettata_il.slice(0, 10).split('-').reverse().join('/')}` : 'non ancora') : '—'}</TableCell>
                  <TableCell className="text-right space-x-1 whitespace-nowrap">
                    {r.user_id && (
                      <>
                        {r.accesso_disallineato && r.login_atteso ? <Button size="sm" variant="default" title="Allinea l'indirizzo di accesso all'anagrafica" disabled={lavoro} onClick={() => riallineaAccesso(r)}><RefreshCw className="h-4 w-4 mr-1" />Allinea accesso</Button> : null}
                        <Button size="sm" variant={r.reset_richiesto_il ? 'default' : 'outline'} title="Imposta o rigenera la password" onClick={() => { setDlgPw(r); setPwSingola(''); setPwGenerata(''); }}><KeyRound className="h-4 w-4" /></Button>
                        <Button size="sm" variant="outline" title="Entra come questo condomino" disabled={lavoro} onClick={() => accediCome(r)}><LogIn className="h-4 w-4" /></Button>
                        {r.stato === 'ATTIVO'
                          ? <Button size="sm" variant="outline" title="Disattiva l'accesso di questo condomino" disabled={lavoro} onClick={() => cambiaStato(r, false)}><UserX className="h-4 w-4" /></Button>
                          : <Button size="sm" variant="outline" title="Riattiva l'accesso di questo condomino" disabled={lavoro} onClick={() => cambiaStato(r, true)}><UserCheck className="h-4 w-4" /></Button>}
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {dati && righe.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground">Nessuna persona per questo filtro</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={dlgInvio} onOpenChange={(o) => { setDlgInvio(o); if (!o) setConfermoInvio(false); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Inviare la lettera per email?</DialogTitle>
            <DialogDescription>
              Stai per spedire <strong>{lettSel.length}</strong> email, una per ogni condomino scelto, all'indirizzo con cui entra nell'app (PEC dove l'accesso è la PEC).
              Oggetto: «{lett?.oggetto}». Il corpo della mail dice solo che si inviano credenziali e istruzioni e indica il link: la lettera con la password provvisoria è nell'allegato PDF/A. L'invio non si può annullare.
            </DialogDescription>
          </DialogHeader>
          {lettGia.length > 0 && (
            <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
              <strong>Reinvio:</strong> {lettGia.length} di questi condomini hanno già ricevuto la lettera e la riceveranno una seconda volta.
            </div>
          )}
          <label className="flex items-center gap-2 text-sm font-medium">
            <input type="checkbox" checked={confermoInvio} onChange={(e) => setConfermoInvio(e.target.checked)} />
            Confermo di voler inviare ora
          </label>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDlgInvio(false); setConfermoInvio(false); }}>Annulla</Button>
            <Button onClick={inviaLettere} disabled={lavoro || !confermoInvio}>Invia {lettSel.length} email</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={dlgAttiva} onOpenChange={setDlgAttiva}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Attiva gli accessi</DialogTitle>
            <DialogDescription>
              Tutti gli utenti creati ({c.DISATTIVATO || 0}) potranno entrare con la propria email o PEC e questa password. Non parte nessuna comunicazione.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Password provvisoria (almeno 8 caratteri)</Label><Input value={pwAttiva} onChange={(e) => setPwAttiva(e.target.value)} autoComplete="off" /></div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={cambioObbl} onChange={(e) => setCambioObbl(e.target.checked)} />
              Al primo accesso ogni condomino deve scegliere la sua password
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlgAttiva(false)}>Annulla</Button>
            <Button onClick={attiva} disabled={lavoro || pwAttiva.length < 8}>Attiva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(dlgPw)} onOpenChange={(o) => { if (!o) { setDlgPw(null); setPwGenerata(''); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Password di {dlgPw?.nome}</DialogTitle>
            <DialogDescription>Accesso: {dlgPw?.login}. Nessuna email parte da sola: puoi inviarla tu dalla finestra, con conferma, oppure comunicare la password.</DialogDescription>
          </DialogHeader>
          {pwGenerata ? (
            <div className="space-y-2">
              <div className="rounded-md border bg-muted p-3 text-sm">Nuova password (si vede solo adesso): <strong className="select-all">{pwGenerata}</strong></div>
              <Button type="button" variant="outline" size="sm" onClick={() => {
                const testo = `Gentile ${dlgPw?.nome},\nla tua nuova password provvisoria per il portale condominio è: ${pwGenerata}\nEntra su https://condominio.artegosrls.it con ${dlgPw?.login}: al primo accesso ti verrà chiesto di sceglierne una tua.`;
                try { navigator.clipboard.writeText(testo); toast.success('Messaggio copiato: incollalo dove preferisci (WhatsApp, posta personale).'); } catch (e) { toast.error('Copia non riuscita: seleziona la password a mano'); }
              }}><Copy className="h-4 w-4 mr-1" />Copia il messaggio da inviare</Button>
              {(dlgPw?.condiviso_con || []).length > 0 && <p className="text-xs text-amber-800">Attenzione: questo accesso è condiviso con altre persone della scheda; la nuova password vale per tutte.</p>}
              <div className="rounded-md border p-3 space-y-2" data-testid="invio-password-mail">
                <div className="text-sm font-medium">Invia per email a questo condomino</div>
                <p className="text-xs text-muted-foreground">Parte la stessa email della lettera d'invito (con la lettera in PDF/A allegata), con la password appena generata, all'indirizzo di accesso: {dlgPw?.login}. Per le PEC parte dal profilo PEC. Una sola email, a questa persona.</p>
                {invEsito ? <p className={`text-sm ${invEsito.startsWith('Inviata') ? 'text-green-700' : 'text-red-700'}`}>{invEsito}</p> : (
                  <>
                    <label className="flex items-start gap-2 text-sm"><input type="checkbox" className="mt-1" checked={invConferma} onChange={(e) => setInvConferma(e.target.checked)} />
                      <span>Confermo: invia la password a {dlgPw?.nome} ({dlgPw?.login}). L'invio non si può annullare.</span></label>
                    <Button size="sm" disabled={lavoro || !invConferma} onClick={inviaMailPassword} data-testid="invia-password-mail"><Mail className="h-4 w-4 mr-1" />Invia per email</Button>
                  </>
                )}
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div><Label>Scegli la password</Label><Input value={pwSingola} onChange={(e) => setPwSingola(e.target.value)} autoComplete="off" /></div>
              <p className="text-xs text-muted-foreground">Al prossimo accesso il condomino dovrà sceglierne una sua.</p>
            </div>
          )}
          <DialogFooter>
            {!pwGenerata && <Button variant="outline" onClick={() => imposta(true)} disabled={lavoro}>Genera una password</Button>}
            {!pwGenerata && <Button onClick={() => imposta(false)} disabled={lavoro || pwSingola.length < 8}>Imposta</Button>}
            {pwGenerata && <Button onClick={() => { setDlgPw(null); setPwGenerata(''); }}>Chiudi</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CondominiAccessi;
